import { onDestroy } from "@/modules/_";

/**
 * Flip Cards Module
 *
 * Button-based flip cards with hover and click/keyboard support.
 * - mouseenter: open this card and close all others (single-open).
 * - mouseleave: close this card.
 * - Button click / Enter|Space: toggle this card; when opening, close others.
 * Sets IDs, aria-controls, aria-expanded, aria-label, aria-labelledby for WCAG.
 */
export default function (element, dataset) {
  const cards = Array.from(
    element.querySelectorAll(".service-detail_phase_flip-card_card")
  );

  if (!cards.length) {
    return;
  }

  const moduleIndex = Array.from(
    document.querySelectorAll('[data-module="flip-cards"]')
  ).indexOf(element);

  /** @type {Map<Element, { button: HTMLButtonElement, back: HTMLElement }>} */
  const cardRefs = new Map();

  function getOrCreateRefs(card) {
    if (cardRefs.has(card)) return cardRefs.get(card);
    const button = card.querySelector("button.service-detail_phase_flip-card_card_face");
    const back = card.querySelector(".service-detail_phase_flip-card_card_face.is-back");
    if (!button || !back) return null;
    const ref = { button, back };
    cardRefs.set(card, ref);
    return ref;
  }

  function openCard(card) {
    const ref = getOrCreateRefs(card);
    if (!ref) return;
    card.classList.add("is-open");
    ref.button.setAttribute("aria-expanded", "true");
  }

  function closeCard(card) {
    const ref = getOrCreateRefs(card);
    if (!ref) return;
    card.classList.remove("is-open");
    ref.button.setAttribute("aria-expanded", "false");
  }

  function closeAllExcept(card) {
    cards.forEach((c) => {
      if (c !== card) closeCard(c);
    });
  }

  function handleMouseEnter(e) {
    const card = e.currentTarget;
    closeAllExcept(card);
    openCard(card);
  }

  function handleMouseLeave(e) {
    closeCard(e.currentTarget);
  }

  function handleButtonClick(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const card = button.closest(".service-detail_phase_flip-card_card");
    if (!card) return;
    const isOpen = card.classList.contains("is-open");
    if (isOpen) {
      closeCard(card);
    } else {
      closeAllExcept(card);
      openCard(card);
    }
  }

  // Initialize ARIA and IDs
  cards.forEach((card, i) => {
    const ref = getOrCreateRefs(card);
    if (!ref) return;

    const buttonId = `flip-card-${moduleIndex}-${i}-trigger`;
    const panelId = `flip-card-${moduleIndex}-${i}-panel`;

    ref.button.id = buttonId;
    ref.button.setAttribute("aria-controls", panelId);
    ref.button.setAttribute("aria-expanded", "false");

    const titleEl = ref.button.querySelector(".service-detail_phase_flip-card_card_face_title");
    const ariaLabel =
      (titleEl && titleEl.textContent?.trim()) || "Karte umdrehen";
    ref.button.setAttribute("aria-label", ariaLabel);

    ref.back.id = panelId;
    ref.back.setAttribute("aria-labelledby", buttonId);

    card.addEventListener("mouseenter", handleMouseEnter);
    card.addEventListener("mouseleave", handleMouseLeave);
    ref.button.addEventListener("click", handleButtonClick);
  });

  onDestroy(() => {
    cards.forEach((card) => {
      const ref = cardRefs.get(card);
      if (!ref) return;
      card.removeEventListener("mouseenter", handleMouseEnter);
      card.removeEventListener("mouseleave", handleMouseLeave);
      ref.button.removeEventListener("click", handleButtonClick);
    });
    cardRefs.clear();
  });
}
